import { useState } from "react";
import { Plus, X, CalendarDays } from "lucide-react";

const DAYS = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
const DAYS_FULL = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];
const HOURS = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
const PRESETS = [
  ["Hari kerja malam", [0, 1, 2, 3, 4], "19:00", "23:00"],
  ["Setiap hari malam", [0, 1, 2, 3, 4, 5, 6], "20:00", "00:00"],
  ["Akhir pekan siang", [5, 6], "10:00", "17:00"],
  ["Sleepcall larut", [0, 1, 2, 3, 4, 5, 6], "22:00", "02:00"],
];
const timeCls = "h-10 px-2 rounded-lg border border-border bg-card text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/40";

export function blocksToAvailability(blocks) {
  const av = {};
  blocks.forEach((b) => b.days.forEach((d) => { (av[String(d)] ||= []).push({ start: b.start, end: b.end }); }));
  return av;
}

function describe(b) {
  const sorted = [...b.days].sort((a, c) => a - c);
  const label = sorted.length === 7 ? "Setiap hari" : sorted.map((d) => DAYS_FULL[d]).join(", ");
  return `${label} ${b.start.replace(":", ".")}–${b.end.replace(":", ".")}`;
}

export function SchedulePicker({ blocks, onChange }) {
  const [days, setDays] = useState([]);
  const [start, setStart] = useState("19:00");
  const [end, setEnd] = useState("23:00");
  const toggleDay = (d) => setDays((ds) => (ds.includes(d) ? ds.filter((x) => x !== d) : [...ds, d]));
  const add = (b) => { onChange([...blocks, b]); setDays([]); };
  const canAdd = days.length > 0 && start !== end;
  return (
    <div className="rounded-lg border border-border bg-muted px-4 py-4" data-testid="schedule-picker">
      <p className="text-sm font-medium text-slate-700 inline-flex items-center gap-2"><CalendarDays size={15} strokeWidth={1.5} className="text-primary" />Jadwal ketersediaan (WIB)</p>
      <p className="mt-1 text-xs text-slate-500">Pilih hari lalu jam mulai dan selesai, klik Tambah. Bisa ditambah lebih dari satu blok. Jam selesai lebih kecil dari jam mulai berarti lewat tengah malam.</p>
      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="schedule-presets">
        {PRESETS.map(([label, d, s, e]) => <button type="button" key={label} onClick={() => add({ days: d, start: s, end: e })} className="px-3 h-8 rounded-full text-xs border border-border bg-card text-slate-600 hover:bg-secondary transition-colors" data-testid={`schedule-preset-${label.replace(/\s+/g, "-").toLowerCase()}`}>+ {label}</button>)}
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5" data-testid="schedule-days">
        {DAYS.map((d, i) => <button type="button" key={d} onClick={() => toggleDay(i)} className={`w-11 h-10 rounded-full text-sm font-medium border transition-colors ${days.includes(i) ? "bg-primary !text-white border-primary" : "bg-card border-border text-slate-600 hover:bg-secondary"}`} aria-pressed={days.includes(i)} data-testid={`schedule-day-${i}`}>{d}</button>)}
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <select value={start} onChange={(e) => setStart(e.target.value)} className={timeCls} data-testid="schedule-start">{HOURS.map((h) => <option key={h} value={h}>{h}</option>)}</select>
        <span className="text-slate-400 text-sm">sampai</span>
        <select value={end} onChange={(e) => setEnd(e.target.value)} className={timeCls} data-testid="schedule-end">{HOURS.map((h) => <option key={h} value={h}>{h}</option>)}</select>
        <button type="button" onClick={() => add({ days, start, end })} disabled={!canAdd} className="btn-outline h-10 px-4 text-sm disabled:opacity-40" data-testid="schedule-add"><Plus size={15} strokeWidth={1.5} />Tambah</button>
      </div>
      <ul className="mt-3 space-y-1.5" data-testid="schedule-blocks">
        {blocks.length === 0 && <li className="text-xs text-amber-700" data-testid="schedule-empty">Belum ada jadwal. Pilih minimal satu blok.</li>}
        {blocks.map((b, i) => (
          <li key={i} className="flex items-center justify-between gap-2 rounded-lg bg-card border border-border px-3 py-2 text-sm text-slate-700" data-testid={`schedule-block-${i}`}>
            <span>{describe(b)}</span>
            <button type="button" onClick={() => onChange(blocks.filter((_, j) => j !== i))} className="p-1 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors" aria-label="Hapus" data-testid={`schedule-remove-${i}`}><X size={14} strokeWidth={1.5} /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}
